/**
 * LandingMinimalist - Professional Minimalist Landing Page
 * Design: 2-color palette with monochromatic line icons
 * Primary: #2563eb (Professional Blue)
 * Accent: #0ea5e9 (Light Blue)
 * 
 * This is a design preview page - no auto-redirect for logged-in users
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../useAuth';
import { usePageTitle } from '../../hooks/usePageTitle';
import SEOHelmet from '../../components/SEOHelmet';

const LandingMinimalist: React.FC = () => {
  usePageTitle('CEC Track - Professional License Management');
  const { user } = useAuth();

  return (
    <>
      <SEOHelmet
        title="[Design Preview] CEC Track - Professional Continuing Education Management"
        description="Internal design preview page - not for production use"
        keywords="continuing education, CEC tracking, license management"
        canonical="https://cectrack.com/"
        noindex={true}
      />

      <div style={{ 
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        color: '#1e293b',
        lineHeight: '1.6'
      }}>
        {/* Hero Section */}
        <section style={{
          backgroundColor: '#ffffff',
          padding: '120px 0 100px',
          borderBottom: '1px solid #e2e8f0'
        }}>
          <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
            <div className="row align-items-center">
              <div className="col-lg-6">
                <div style={{ marginBottom: '24px' }}>
                  <span style={{
                    display: 'inline-block',
                    padding: '6px 16px',
                    backgroundColor: '#eff6ff',
                    color: '#2563eb',
                    fontSize: '14px',
                    fontWeight: '600',
                    borderRadius: '20px',
                    letterSpacing: '0.5px'
                  }}>
                    PROFESSIONAL LICENSE MANAGEMENT
                  </span>
                </div>
                <h1 style={{
                  fontSize: '56px',
                  fontWeight: '700',
                  lineHeight: '1.1',
                  marginBottom: '24px',
                  color: '#0f172a',
                  letterSpacing: '-0.02em'
                }}>
                  Never Miss a License Renewal Again
                </h1>
                <p style={{
                  fontSize: '20px',
                  lineHeight: '1.7',
                  color: '#475569',
                  marginBottom: '40px',
                  maxWidth: '540px'
                }}>
                  Track continuing education credits across all states in one centralized platform. 
                  Stay compliant, organized, and ready for renewal.
                </p>
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                  <Link
                    to={user ? "/dashboard" : "/register"}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '16px 32px',
                      backgroundColor: '#2563eb',
                      color: '#ffffff',
                      fontSize: '16px',
                      fontWeight: '600',
                      borderRadius: '8px',
                      textDecoration: 'none',
                      transition: 'all 0.2s ease',
                      border: 'none',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#1d4ed8';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 10px 25px -5px rgba(37, 99, 235, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#2563eb';
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    {user ? "Go to Dashboard" : "Get Started Free"}
                    <svg style={{ marginLeft: '8px', width: '20px', height: '20px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </Link>
                  <Link
                    to="/how-it-works"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '16px 32px',
                      backgroundColor: 'transparent',
                      color: '#2563eb',
                      fontSize: '16px',
                      fontWeight: '600',
                      borderRadius: '8px',
                      textDecoration: 'none',
                      border: '2px solid #e2e8f0',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#2563eb';
                      e.currentTarget.style.backgroundColor = '#f8fafc';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#e2e8f0';
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    Learn More
                  </Link>
                </div>
                <div style={{ marginTop: '40px', display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <svg style={{ width: '20px', height: '20px', color: '#0ea5e9' }} fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                    </svg>
                    <span style={{ fontSize: '14px', color: '#64748b', fontWeight: '500' }}>Free forever</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <svg style={{ width: '20px', height: '20px', color: '#0ea5e9' }} fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                    </svg>
                    <span style={{ fontSize: '14px', color: '#64748b', fontWeight: '500' }}>No credit card required</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <svg style={{ width: '20px', height: '20px', color: '#0ea5e9' }} fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                    </svg>
                    <span style={{ fontSize: '14px', color: '#64748b', fontWeight: '500' }}>Setup in 5 minutes</span>
                  </div>
                </div>
              </div>
              <div className="col-lg-6 d-none d-lg-block">
                <div style={{
                  padding: '48px',
                  backgroundColor: '#f8fafc',
                  borderRadius: '16px',
                  border: '1px solid #e2e8f0'
                }}>
                  {/* Stats Grid */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                    <div style={{ padding: '24px', backgroundColor: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                      <div style={{ fontSize: '32px', fontWeight: '700', color: '#2563eb', marginBottom: '8px' }}>50+</div>
                      <div style={{ fontSize: '14px', color: '#64748b', fontWeight: '500' }}>States Supported</div>
                    </div>
                    <div style={{ padding: '24px', backgroundColor: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                      <div style={{ fontSize: '32px', fontWeight: '700', color: '#2563eb', marginBottom: '8px' }}>30+</div>
                      <div style={{ fontSize: '14px', color: '#64748b', fontWeight: '500' }}>Professions</div>
                    </div>
                    <div style={{ padding: '24px', backgroundColor: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                      <div style={{ fontSize: '32px', fontWeight: '700', color: '#2563eb', marginBottom: '8px' }}>1000+</div>
                      <div style={{ fontSize: '14px', color: '#64748b', fontWeight: '500' }}>Active Users</div>
                    </div>
                    <div style={{ padding: '24px', backgroundColor: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                      <div style={{ fontSize: '32px', fontWeight: '700', color: '#2563eb', marginBottom: '8px' }}>99%</div>
                      <div style={{ fontSize: '14px', color: '#64748b', fontWeight: '500' }}>On-Time Renewals</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section style={{ padding: '120px 0', backgroundColor: '#ffffff' }}>
          <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
            <div style={{ textAlign: 'center', marginBottom: '80px', maxWidth: '700px', margin: '0 auto 80px' }}>
              <h2 style={{
                fontSize: '42px',
                fontWeight: '700',
                lineHeight: '1.2',
                marginBottom: '16px',
                color: '#0f172a',
                letterSpacing: '-0.02em'
              }}>
                Everything You Need to Stay Compliant
              </h2>
              <p style={{ fontSize: '18px', color: '#64748b', lineHeight: '1.7' }}>
                Professional-grade tools designed for licensed professionals managing multi-state requirements
              </p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '32px' }}>
              {[
                {
                  icon: (
                    <svg style={{ width: '24px', height: '24px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  ),
                  title: 'Multi-State Tracking',
                  description: 'Manage licenses and CEC requirements across multiple states from a single dashboard. See all renewal cycles at a glance.'
                },
                {
                  icon: (
                    <svg style={{ width: '24px', height: '24px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  ),
                  title: 'Certificate Storage',
                  description: 'Securely store all course certificates in the cloud. Access them anytime, anywhere, and never lose important documentation.'
                },
                {
                  icon: (
                    <svg style={{ width: '24px', height: '24px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                  ),
                  title: 'Smart Reminders',
                  description: 'Receive timely notifications about upcoming deadlines and missing credits. Stay ahead of requirements effortlessly.'
                },
                {
                  icon: (
                    <svg style={{ width: '24px', height: '24px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                    </svg>
                  ),
                  title: 'Compliance Dashboard',
                  description: 'Real-time compliance status for each license. Visual progress tracking shows exactly where you stand for every renewal cycle.'
                },
                {
                  icon: (
                    <svg style={{ width: '24px', height: '24px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  ),
                  title: 'Secure & Private',
                  description: 'Bank-level encryption protects your data. Your certificates and personal information are always safe and confidential.'
                },
                {
                  icon: (
                    <svg style={{ width: '24px', height: '24px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  ),
                  title: 'Lightning Fast',
                  description: 'Upload certificates and get instant credit calculations. No waiting, no manual data entry - just upload and done.'
                }
              ].map((feature, index) => (
                <div
                  key={index}
                  style={{
                    padding: '32px',
                    backgroundColor: '#ffffff',
                    borderRadius: '12px',
                    border: '1px solid #e2e8f0',
                    transition: 'all 0.3s ease',
                    cursor: 'default'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#2563eb';
                    e.currentTarget.style.boxShadow = '0 10px 40px -10px rgba(37, 99, 235, 0.15)';
                    e.currentTarget.style.transform = 'translateY(-4px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#e2e8f0';
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    backgroundColor: '#eff6ff',
                    color: '#2563eb',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '20px'
                  }}>
                    {feature.icon}
                  </div>
                  <h3 style={{
                    fontSize: '20px',
                    fontWeight: '600',
                    marginBottom: '12px',
                    color: '#0f172a'
                  }}>
                    {feature.title}
                  </h3>
                  <p style={{
                    fontSize: '15px',
                    color: '#64748b',
                    lineHeight: '1.7',
                    margin: 0
                  }}>
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section style={{ padding: '120px 0', backgroundColor: '#f8fafc' }}>
          <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
            <div style={{ textAlign: 'center', marginBottom: '80px' }}>
              <h2 style={{
                fontSize: '42px',
                fontWeight: '700',
                lineHeight: '1.2',
                marginBottom: '16px',
                color: '#0f172a',
                letterSpacing: '-0.02em'
              }}>
                Simple Process, Powerful Results
              </h2>
              <p style={{ fontSize: '18px', color: '#64748b', lineHeight: '1.7' }}>
                Get started in minutes with our streamlined workflow
              </p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '48px', position: 'relative' }}>
              {[
                {
                  step: '01',
                  title: 'Add Your Licenses',
                  description: 'Enter your professional licenses and state requirements. We support 30+ professions across all 50 states.'
                },
                {
                  step: '02',
                  title: 'Upload Certificates',
                  description: 'Add completed CE classes with certificates. Drag and drop files or take photos with your phone.'
                },
                {
                  step: '03',
                  title: 'Track Progress',
                  description: 'Monitor compliance status in real-time. See exactly what you need for each renewal cycle.'
                },
                {
                  step: '04',
                  title: 'Stay Compliant',
                  description: 'Receive timely reminders and never miss a deadline. Renew with confidence every time.'
                }
              ].map((item, index) => (
                <div key={index} style={{ position: 'relative', textAlign: 'center' }}>
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '64px',
                    height: '64px',
                    borderRadius: '50%',
                    backgroundColor: '#2563eb',
                    color: '#ffffff',
                    fontSize: '20px',
                    fontWeight: '700',
                    marginBottom: '24px',
                    position: 'relative',
                    zIndex: 1
                  }}>
                    {item.step}
                  </div>
                  <h3 style={{
                    fontSize: '20px',
                    fontWeight: '600',
                    marginBottom: '12px',
                    color: '#0f172a'
                  }}>
                    {item.title}
                  </h3>
                  <p style={{
                    fontSize: '15px',
                    color: '#64748b',
                    lineHeight: '1.7',
                    margin: 0
                  }}>
                    {item.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Professions Section */}
        <section style={{ padding: '120px 0', backgroundColor: '#ffffff' }}>
          <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
            <div style={{ textAlign: 'center', marginBottom: '64px' }}>
              <h2 style={{
                fontSize: '42px',
                fontWeight: '700',
                lineHeight: '1.2',
                marginBottom: '16px',
                color: '#0f172a',
                letterSpacing: '-0.02em'
              }}>
                Built for Licensed Professionals
              </h2>
              <p style={{ fontSize: '18px', color: '#64748b', lineHeight: '1.7' }}>
                Supporting 30+ professions across all 50 states
              </p>
            </div>

            <div style={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: '12px', 
              justifyContent: 'center',
              maxWidth: '900px',
              margin: '0 auto'
            }}>
              {[
                'Architects',
                'Professional Engineers',
                'Landscape Architects',
                'CPAs',
                'Registered Nurses',
                'Social Workers',
                'Real Estate Agents',
                'Attorneys',
                'Dentists',
                'Physical Therapists',
                'Counselors',
                'Appraisers',
                'Land Surveyors',
                'Dental Hygienists',
                'Interior Designers'
              ].map((profession, index) => (
                <div
                  key={index}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#475569',
                    transition: 'all 0.2s ease',
                    cursor: 'default'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#eff6ff';
                    e.currentTarget.style.borderColor = '#2563eb';
                    e.currentTarget.style.color = '#2563eb';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#f8fafc';
                    e.currentTarget.style.borderColor = '#e2e8f0';
                    e.currentTarget.style.color = '#475569';
                  }}
                >
                  {profession}
                </div>
              ))}
            </div>

            <div style={{ textAlign: 'center', marginTop: '48px' }}>
              <p style={{ fontSize: '15px', color: '#64748b' }}>
                ...and many more licensed professionals requiring continuing education credits
              </p>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section style={{
          padding: '100px 0',
          backgroundColor: '#0f172a',
          color: '#ffffff'
        }}>
          <div className="container" style={{ maxWidth: '800px', margin: '0 auto', padding: '0 24px', textAlign: 'center' }}>
            <h2 style={{
              fontSize: '42px',
              fontWeight: '700',
              lineHeight: '1.2',
              marginBottom: '20px',
              letterSpacing: '-0.02em'
            }}>
              Ready to Simplify Your License Renewals?
            </h2>
            <p style={{
              fontSize: '18px',
              lineHeight: '1.7',
              color: '#cbd5e1',
              marginBottom: '40px'
            }}>
              Join hundreds of licensed professionals who trust CEC Track to manage their continuing education
            </p>
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link
                to={user ? "/dashboard" : "/register"}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '16px 32px',
                  backgroundColor: '#2563eb',
                  color: '#ffffff',
                  fontSize: '16px',
                  fontWeight: '600',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#1d4ed8';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#2563eb';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                {user ? "Go to Dashboard" : "Get Started Free"}
                <svg style={{ marginLeft: '8px', width: '20px', height: '20px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
              <Link
                to="/how-it-works"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '16px 32px',
                  backgroundColor: 'transparent',
                  color: '#ffffff',
                  fontSize: '16px',
                  fontWeight: '600',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  border: '2px solid #334155',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#2563eb';
                  e.currentTarget.style.backgroundColor = 'rgba(37, 99, 235, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#334155';
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                Learn More
              </Link>
            </div>
            <div style={{ marginTop: '32px' }}>
              <p style={{ fontSize: '14px', color: '#94a3b8' }}>
                Already have an account?{' '}
                <Link to="/login" style={{ color: '#60a5fa', textDecoration: 'none', fontWeight: '500' }}>
                  Sign In →
                </Link>
              </p>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer style={{
          padding: '48px 0',
          backgroundColor: '#ffffff',
          borderTop: '1px solid #e2e8f0'
        }}>
          <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
            <div style={{ textAlign: 'center' }}>
              <p style={{ fontSize: '14px', color: '#64748b', margin: 0 }}>
                © 2024 CEC Track. All rights reserved.
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default LandingMinimalist;
